import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { trackSoftConversionEvent } from '../../actions/tracking';
import ExtendableGroupAuto from '../../components/extendable-group/extendable-group-auto';
import Link from '../../components/link';
import { asHtml } from '../../helpers/markdown';
import { getItem } from '../../selectors/item';
import { getRetailerAbout, getRetailerDocument } from '../../selectors/retailer';
import { softConversionEvents } from '../../shared/tracking';
import { H3 } from '../../styled-components/components/Heading';
import { Stack, StackMedium } from '../../styled-components/components/Stack';
import BranchAddress from './branch-address';
import OpeningHours from './branch-opening-hours';
import BranchTelephone from './branch-telephone';

const BranchAbout = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const data = useSelector(getRetailerAbout);
  const retailer = useSelector(getRetailerDocument);
  const item = useSelector(getItem);
  const track = bindActionCreators(trackSoftConversionEvent, dispatch);

  const contentRef = useRef<HTMLDivElement>(null);
  const [trackingToggle, setTrackingToggle] = useState(false);

  if (!data || !retailer) return null;

  const { name, city, logo, description, link } = data;

  const onTrackToggle = () => {
    if (!trackingToggle) {
      track(item, softConversionEvents.SOFT_CONVERSIONS_ITEM_DEALER_READ_MORE_CLICK);
      setTrackingToggle(true);
    }
  };

  return (
    <>
      <StackMedium>
        <div data-am-columnrow="always">
          <div className="column">
            <H3 as="h2" $noMargin>
              {name}
            </H3>
          </div>
          {logo && (
            <div className="column minimal valign-top">
              <img
                src={`${logo}?w=80&format=png`}
                srcSet={`${logo}?w=160&format=png 2x`}
                className="l-block"
                alt={name}
              />
            </div>
          )}
        </div>
      </StackMedium>
      <StackMedium>
        <Stack>
          <div className="border-top border-bottom p-y font-medium font-size-small">
            <i className="icon-map-pin m-r-mini text-action" />
            {city}
          </div>
        </Stack>
        {description && (
          <Stack>
            <ExtendableGroupAuto
              label={t('Read more', { ns: 'common' })}
              childRef={contentRef}
              onToggle={onTrackToggle}
            >
              <div
                data-am-content=""
                ref={contentRef}
                dangerouslySetInnerHTML={{ __html: asHtml(description) }}
              />
            </ExtendableGroupAuto>
          </Stack>
        )}
        <Stack>
          <Link
            onClick={() =>
              track(item, softConversionEvents.SOFT_CONVERSIONS_ITEM_DEALER_PAGE_CLICK)
            }
            to={link}
            data-am-link=""
            title={t('Read more about this', { ns: 'common', name })}
          >
            {t('Read more about this', { ns: 'common', name })}
          </Link>
        </Stack>
        <Stack>
          <div data-wx-detaillist="">
            <ul className="detail-list">
              <BranchAddress />
              <OpeningHours />
              <BranchTelephone />
            </ul>
          </div>
        </Stack>
      </StackMedium>
    </>
  );
};

export default BranchAbout;
